import { forwardRef, Inject, Injectable, Optional, Renderer2, RendererFactory2 } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, combineLatestWith, Observable, of } from 'rxjs';
import { filter, map, shareReplay, take } from 'rxjs/operators';
import { MmuUserService } from '../auth/user-service/mmu-user.service';
import { MmuCookiePreferences } from '../cookies/mmu-cookie-preferences';
import { MmuCookiePreferencesService } from '../cookies/service/mmu-cookie-preferences.service';
import { FeatureFlagsMap, MmuFeatureFlagsService } from '../feature-flags';
import { WINDOW } from '../injection-tokens/window.token';
import { MMU_GOOGLE_TAG_MANAGER_TRACKING_ID } from './mmu-google-tag-manager-tracking-id';


@Injectable({
  providedIn: 'root'
})
export class MmuAnalyticsService {

  /**
   * @deprecated Readiness gets handled by each tracking method internally, this method is for testing purposes
   */
  get ready(): Observable<boolean> {
    return this._gaInitialized.asObservable();
  }

  get analyticsTrackingId(): string {
    return this._analyticsTrackingId || '';
  }

  get dataLayer(): any[] {
    const window = this.windowToken;
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
  }

  private readonly _analyticsTrackingId: string | null = '';
  private navigationEvents: Observable<Event> = of();
  private _gaInitialized = new BehaviorSubject<boolean>(false);

  private scriptElement?: HTMLScriptElement;

  private readonly renderer: Renderer2;

  constructor(
    private _router: Router,
    private mmuUserService: MmuUserService,
    // use forwardRef here to resolve circular dependency between MmuAnalyticsService and MmuCookiePreferencesService - both depend on each other
    @Inject(forwardRef(() => MmuCookiePreferencesService)) private mmuCookiePreferencesService: MmuCookiePreferencesService,
    private rendererFactory: RendererFactory2,
    private featureFlagsService: MmuFeatureFlagsService,
    @Inject(WINDOW) private windowToken: Window,
    @Optional() @Inject(MMU_GOOGLE_TAG_MANAGER_TRACKING_ID) private googleTagManagerTrackingId: string | null
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this._analyticsTrackingId = this.googleTagManagerTrackingId;

    this.mmuUserService.user$.subscribe((userDetails) => {
      if (userDetails?.internal) {
        this.disableTracking();
      }
    });

    this.mmuCookiePreferencesService.settings$.pipe(
      filter((cookiePreferences: MmuCookiePreferences | null) => cookiePreferences !== null && cookiePreferences.statistics),
      take(1),
      combineLatestWith(this.mmuUserService.user$.pipe(map((user) => user.internal)))
    ).subscribe(([_cookiePrefs, internal]) => {
      if (this.newTrackingEnabled() && internal == false) {
        this.loadTrackingScriptFromRemote(`https://www.googletagmanager.com/gtm.js?id=${this._analyticsTrackingId}`);
      }
    });


    /* Since the Google Analytics script can load for a while, we keep a buffer of 10 navigation events that will be tracked as
     * soon as GA has finished loading. That way it is unlikely miss any navigation events. */
    this.navigationEvents = this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      shareReplay(10)
    );

    // We need to subscribe this observable once so that the shareReplay operator starts buffering
    this.navigationEvents.pipe(take(1)).subscribe();
  }

  /**
   * @deprecated entGTMTracking
   * @returns boolean
   */
  newTrackingEnabled = (): boolean => {
    return this.featureFlagsService.isFeatureEnabled(FeatureFlagsMap.FFEntGTMTracking);
  };

  /**
   * @deprecated This method is deprecated, service gets initialized by constructor on instantiation
   * @param _analyticsTrackingId @deprecated provide analytics tracking id to use this service
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  init(_analyticsTrackingId?: string): void {
  }

  /**
   *
   * @deprecated This method cannot send additional custom data, use emitCustomEvent in the future
   * @param eventCategory
   * @param eventAction
   * @param eventLabel
   * @param dry Just log to console instead of tracking it by GA, for debugging purpose
   */
  emitEvent(eventCategory: string, eventAction: string, eventLabel?: string, dry = false): void {
    this.ready.pipe(
      filter((ready) => ready == true),
      take(1)
    ).subscribe(() => {
      if (dry) {
        console.log('Tracking', 'event', eventCategory, eventAction, eventLabel);
      } else {
        if (this.newTrackingEnabled()) {
          this.dataLayer.push({
              event: eventCategory,
              eventAction,
              eventLabel
            }
          );
        }
      }
    });
  }

  /**
   *
   * @param eventCategory
   * @param eventAction
   * @param customData An object of custom data elements
   * @param dry Just log to console instead of tracking it by GA, for debugging purpose
   */
  emitCustomEvent(eventCategory: string, eventAction: string, customData?: Record<string, string | number>, dry = false): void {
    this.ready.pipe(
      filter((ready) => ready == true),
      take(1)
    ).subscribe(() => {
      if (dry) {
        console.log('Tracking', 'event', eventCategory, eventAction, customData);
      } else {
        if (this.newTrackingEnabled()) {
          this.dataLayer.push({ event: eventCategory, eventAction, ...customData });
        }
      }
    });
  }

  disableTracking(): void {
    if (this.newTrackingEnabled()) {
      this._gaInitialized.next(false);
    }
  }


  private loadTrackingScriptFromRemote(scriptUrl: string): void {
    if (!this.scriptElement) {
      this.scriptElement = this.renderer.createElement('script') as HTMLScriptElement;
      this.scriptElement.id = 'gtm-script';
      this.scriptElement.src = scriptUrl;
      this.scriptElement.async = true;

      this.scriptElement.onload = async (): Promise<void> => {
        if (this.newTrackingEnabled()) {
          console.log('gtm.js loaded, going to set it up');
          await this.setupGTMSession();
        }
      };
      this.renderer.appendChild(document.head, this.scriptElement);
    }
  }

  private setupGTMSession(): Promise<void> {
    return new Promise<void>(resolve => {
      this.dataLayer.push({
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });
      this.dataLayer.push({
        event: 'config',
        /* eslint-disable-next-line @typescript-eslint/naming-convention */
        'google_tag_params': {
          /* eslint-disable-next-line @typescript-eslint/naming-convention */
          'host_name': document.location.hostname,
          /* eslint-disable-next-line @typescript-eslint/naming-convention */
          'user_id': this.mmuUserService.user.auid ?? '',
          /* eslint-disable-next-line @typescript-eslint/naming-convention */
          'user_internal': this.mmuUserService.user.internal ?? false
        }
      });

      // Make sure the userId is always up-to-date in Google Analytics
      this.mmuUserService.user$.pipe(filter((user) => user !== null)).subscribe((userDetails) => {
        // https://developers.google.com/tag-platform/gtagjs/configure#set_up_data_collection
        this.dataLayer.push({
          event: 'set',
          /* eslint-disable-next-line @typescript-eslint/naming-convention */
          'user_id': userDetails.auid,
          /* eslint-disable-next-line @typescript-eslint/naming-convention */
          'user_internal': userDetails.internal ?? false

        });
      });

      this._gaInitialized.next(true);

      this.registerToRouting();
      resolve();
    });
  }


  private registerToRouting(): void {
    this.navigationEvents.subscribe((event: any) => {
      if (this.newTrackingEnabled()) {
        const finalUrl: string = event['urlAfterRedirects'];
        this.dataLayer.push({ event: 'set', 'page': finalUrl });
        /* eslint-disable-next-line @typescript-eslint/naming-convention */
        this.dataLayer.push({ event: 'page_view', 'page_location': finalUrl });
      }
    });
  }


}
